/**
 * PhraseStep Component
 *
 * Registration step 1: Show and save recovery phrase.
 * Mobile-optimized with larger touch targets.
 */

import { Button } from '@sisumail/ui';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Copy, Download, Eye, EyeOff, Shield } from 'lucide-react';
import { useState } from 'react';

interface PhraseStepProps {
    username: string;
    phrase: string;
    onNext: () => void;
    onBack?: () => void;
}

export function PhraseStep({ username, phrase, onNext, onBack }: PhraseStepProps) {
    const [showPhrase, setShowPhrase] = useState(true);
    const [copied, setCopied] = useState(false);
    const [saved, setSaved] = useState(false);
    const [consentTerms, setConsentTerms] = useState(false);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(phrase);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleSaveFile = () => {
        const content = `SISUMAIL RECOVERY PHRASE\n========================\nAccount: ${username}@sisumail.fi\nCreated: ${new Date().toISOString()}\n\n${phrase}\n\n⚠️ KEEP THIS SAFE!`;
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sisumail-${username}-recovery.txt`;
        a.click();
        URL.revokeObjectURL(url);
        setSaved(true);
    };

    return (
        <motion.div
            key="phrase"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="mx-auto w-full max-w-lg"
        >
            <div className="overflow-hidden rounded-3xl border border-neutral-800 bg-neutral-900 shadow-2xl">
                {/* Header */}
                <div className="border-neutral-800 border-b bg-neutral-950/50 p-8 text-center sm:p-10">
                    <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border border-emerald-500/20 bg-emerald-500/10 shadow-2xl shadow-emerald-500/5 transition-all">
                        <Shield className="h-8 w-8 text-emerald-500" />
                    </div>
                    <h2 className="mb-3 font-bold text-3xl text-white tracking-tight">
                        Save Your Key
                    </h2>
                    <p className="px-4 font-medium text-neutral-500 text-sm leading-relaxed">
                        This phrase is the <strong className="text-emerald-500">only way</strong> to
                        access your account.
                        <br />
                        We do not store it. If you lose it, it's gone forever.
                    </p>
                </div>

                {/* Phrase Area */}
                <div className="space-y-8 p-8 sm:p-10">
                    {/* Identity Chip */}
                    <div className="flex justify-center">
                        <div className="inline-flex items-center gap-3 rounded-full border border-neutral-800 bg-neutral-950 px-4 py-2 font-bold text-neutral-400 text-xs uppercase tracking-[0.15em]">
                            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
                            {username}@sisumail.fi
                        </div>
                    </div>

                    {/* Words Grid */}
                    <div className="group relative">
                        <div
                            className={`grid grid-cols-3 gap-3 rounded-2xl border bg-neutral-950 p-6 ${showPhrase ? 'border-neutral-800 border-dashed' : 'border-neutral-900'} transition-all`}
                        >
                            {phrase.split(' ').map((word, i) => (
                                <div key={i} className="flex items-center gap-2.5 rounded-lg p-1">
                                    <span className="w-4 select-none font-bold text-[10px] text-neutral-700">
                                        {(i + 1).toString().padStart(2, '0')}
                                    </span>
                                    <span
                                        className={`font-bold font-mono text-sm tracking-tight ${showPhrase ? 'text-white' : 'select-none text-neutral-800 blur-sm'}`}
                                    >
                                        {word}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* Blur Overlay Trigger */}
                        <div className="absolute -top-3 -right-3">
                            <button
                                onClick={() => setShowPhrase(!showPhrase)}
                                className="flex h-10 w-10 items-center justify-center rounded-xl border border-neutral-700 bg-neutral-800 text-neutral-400 shadow-xl transition-all hover:border-emerald-500 hover:text-white active:scale-95"
                            >
                                {showPhrase ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    {/* Action Bar */}
                    <div className="grid grid-cols-2 gap-4">
                        <button
                            onClick={handleCopy}
                            className={`flex items-center justify-center gap-3 rounded-xl border px-4 py-4 font-bold text-[10px] uppercase tracking-widest transition-all active:scale-95 ${
                                copied
                                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-500'
                                    : 'border-neutral-800 bg-neutral-950 text-neutral-400 hover:bg-neutral-900 hover:text-white'
                            }`}
                        >
                            {copied ? <Check size={14} /> : <Copy size={14} />}
                            {copied ? 'Copied' : 'Copy Phrase'}
                        </button>

                        <button
                            onClick={handleSaveFile}
                            className={`flex items-center justify-center gap-3 rounded-xl border px-4 py-4 font-bold text-[10px] uppercase tracking-widest transition-all active:scale-95 ${
                                saved
                                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-500'
                                    : 'border-neutral-800 bg-neutral-950 text-neutral-400 hover:bg-neutral-900 hover:text-white'
                            }`}
                        >
                            {saved ? <Check size={14} /> : <Download size={14} />}
                            {saved ? 'Saved' : 'Save File'}
                        </button>
                    </div>

                    {/* Consent */}
                    <label className="group flex cursor-pointer items-start gap-4 rounded-2xl border border-amber-500/10 bg-amber-500/5 p-5 transition-all hover:bg-amber-500/[0.08]">
                        <div className="relative mt-0.5">
                            <input
                                type="checkbox"
                                checked={consentTerms}
                                onChange={(e) => setConsentTerms(e.target.checked)}
                                className="h-5 w-5 cursor-pointer rounded-md border-neutral-800 bg-neutral-950 text-emerald-500 transition-all focus:ring-emerald-500/20"
                            />
                        </div>
                        <span className="select-none font-medium text-neutral-500 text-xs leading-relaxed transition-colors group-hover:text-neutral-400">
                            I understand that if I lose this phrase, I lose access to{' '}
                            <strong className="text-white">{username}@sisumail.fi</strong> and all
                            its signals forever.
                        </span>
                    </label>

                    {/* Next Button */}
                    <Button
                        onClick={onNext}
                        disabled={!consentTerms}
                        className="h-16 w-full font-bold text-lg uppercase tracking-widest"
                        variant="primary"
                    >
                        I Have Saved It <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>

                    {onBack && (
                        <button
                            onClick={onBack}
                            className="w-full py-2 font-bold text-[10px] text-neutral-600 uppercase tracking-widest transition-colors hover:text-white"
                        >
                            Back to Username
                        </button>
                    )}
                </div>
            </div>
        </motion.div>
    );
}
